import React from 'react';
import { motion } from 'framer-motion';
import { ScrollReveal } from './ScrollReveal';
import { CTAButton } from './CTAButton';
import { Award, TrendingUp, Users } from 'lucide-react';
export function AboutAuthor() {
  return <section className="py-24 px-4 bg-[#050510] relative overflow-hidden" data-id="element-18">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-19">
        <div className="absolute top-40 -left-40 w-96 h-96 bg-cyan-900/20 rounded-full blur-[120px]" data-id="element-20" />
        <div className="absolute bottom-40 -right-40 w-96 h-96 bg-purple-900/20 rounded-full blur-[120px]" data-id="element-21" />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10" data-id="element-22">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-23">
          {/* LEFT: Text Content */}
          <div className="order-2 lg:order-1" data-id="element-24">
            <ScrollReveal data-id="element-25">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6" data-id="element-26">
                <Award className="w-4 h-4 text-cyan-400" data-id="element-27" />
                <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400" data-id="element-28">
                  Sobre el Autor
                </span>
              </div>

              <h2 className="text-4xl md:text-5xl font-extrabold leading-tight mb-2" data-id="element-29">
                Daniel S.
              </h2>
              <p className="text-xl text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 font-semibold mb-8" data-id="element-30">
                Digital Marketer • 12 años de experiencia
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-31">
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed" data-id="element-32">
                <p data-id="element-33">
                  Durante más de una década he trabajado creando estrategias,
                  campañas y sistemas de venta que{' '}
                  <strong className="text-white" data-id="element-34">
                    transforman atención en dinero
                  </strong>
                  .
                </p>
                <p data-id="element-35">
                  A través de mi agencia de marketing he ayudado a marcas
                  personales y negocios a posicionarse, vender y escalar usando
                  el poder del contenido y la publicidad digital.
                </p>

                <div className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 my-8 relative overflow-hidden" data-id="element-36">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-500 to-purple-500" data-id="element-37" />
                  <p className="italic text-gray-200" data-id="element-38">
                    "Pero después de años viendo cómo funcionan realmente los
                    negocios online, entendí algo muy claro:{' '}
                    <strong className="text-white" data-id="element-39">
                      La mayoría de las personas no falla por falta de talento.
                      Falla porque no tiene estructura para monetizar lo que
                      sabe.
                    </strong>
                    "
                  </p>
                </div>

                <p data-id="element-40">
                  Por eso creé este programa. Aquí no vienes a motivarte. Vienes
                  a construir un{' '}
                  <strong className="text-cyan-400" data-id="element-41">activo digital</strong> que
                  pueda generarte ingresos.
                </p>
                <p data-id="element-42">
                  Yo te voy a mostrar el camino porque ya lo he hecho con marcas
                  y con proyectos reales.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4} className="mt-10" data-id="element-43">
              <h3 className="text-2xl font-bold text-white mb-6" data-id="element-44">
                Ahora la única pregunta es esta:
              </h3>
              <p className="text-xl text-gray-400 mb-8" data-id="element-45">
                ¿Vas a seguir viendo contenido… o vas a empezar a monetizarlo?{' '}
                <br data-id="element-46" />
                <span className="text-white font-semibold" data-id="element-47">
                  La decisión es tuya.
                </span>
              </p>
              <CTAButton size="lg" data-id="element-48" />
            </ScrollReveal>
          </div>

          {/* RIGHT: Image Collage */}
          <div className="order-1 lg:order-2 relative" data-id="element-49">
            <div className="grid grid-cols-2 gap-4 relative" data-id="element-50">
              {/* Decorative elements */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-50 rounded-full mix-blend-overlay" data-id="element-51" />

              {/* Image 1 - Tall */}
              <ScrollReveal delay={0.3} direction="up" className="col-span-1 row-span-2" data-id="element-52">
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl group h-full" data-id="element-53">
                  <div className="absolute inset-0 bg-cyan-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 mix-blend-overlay" data-id="element-54" />
                  <img src="https://cdn.magicpatterns.com/uploads/kDbp1ELJHahjuMYH9ZtHgN/IMG_2454.jpg" alt="Daniel en entrevista" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
                  minHeight: '400px'
                }} data-id="element-55" />
                </div>
              </ScrollReveal>

              {/* Image 2 - Top Right */}
              <ScrollReveal delay={0.5} direction="left" className="col-span-1 h-48 md:h-64" data-id="element-56">
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl group h-full" data-id="element-57">
                  <div className="absolute inset-0 bg-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 mix-blend-overlay" data-id="element-58" />
                  <img src="https://cdn.magicpatterns.com/uploads/98jWgCwtf65JZCaDTbFsEv/IMG_2455.jpg" alt="Daniel con clientes" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 object-top" data-id="element-59" />
                </div>
              </ScrollReveal>

              {/* Image 3 - Bottom Right */}
              <ScrollReveal delay={0.7} direction="left" className="col-span-1 h-48 md:h-64" data-id="element-60">
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl group h-full" data-id="element-61">
                  <div className="absolute inset-0 bg-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 mix-blend-overlay" data-id="element-62" />
                  <img src="https://cdn.magicpatterns.com/uploads/vGBV3uwNxEUEnR6YDg13KS/IMG_2456.jpg" alt="Daniel en evento" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 object-top" data-id="element-63" />
                </div>
              </ScrollReveal>
            </div>

            {/* Floating Stats Card */}
            <ScrollReveal delay={0.9} direction="up" className="absolute -bottom-6 -left-6 md:-left-12 z-20" data-id="element-64">
              <div className="glass-card px-6 py-4 rounded-2xl border border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.5)] flex items-center gap-4 bg-[#0c0c20]/90 backdrop-blur-md" data-id="element-65">
                <div className="p-3 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20" data-id="element-66">
                  <Users className="w-6 h-6 text-cyan-400" data-id="element-67" />
                </div>
                <div data-id="element-68">
                  <p className="text-2xl font-bold text-white" data-id="element-69">12+ Años</p>
                  <p className="text-xs text-gray-400 uppercase tracking-wider" data-id="element-70">
                    Generando Resultados
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>;
}